import { ChevronRight } from "lucide-react";
import { NavLink } from "react-router-dom";
import { cn } from "@/utils";
import type { DropdownNavItem, NavItem } from "../hooks/useNavState";
import {
	getSidebarTheme,
	isDarkVariant,
	type SidebarVariant,
} from "./sidebarTheme";

interface SidebarDropdownItemProps {
	item: DropdownNavItem;
	isCollapsed: boolean;
	variant: SidebarVariant;
	isExpanded: boolean;
	isActive: boolean;
	dropdownKey: string;
	iconRefs: React.MutableRefObject<Record<string, HTMLSpanElement | null>>;
	submenuRefs: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
	onToggle: (path: string) => void;
	isRouteActive: (path: string) => boolean;
}

export const SidebarDropdownItem = ({
	item,
	isCollapsed,
	variant,
	isExpanded,
	isActive,
	dropdownKey,
	iconRefs,
	submenuRefs,
	onToggle,
	isRouteActive,
}: SidebarDropdownItemProps) => {
	const theme = getSidebarTheme(variant);
	const isDark = isDarkVariant(variant);

	const getDropdownButtonClasses = () => {
		const baseClasses = `flex items-center rounded-md ${
			isCollapsed
				? "w-10 h-10 justify-center"
				: "px-2 py-1.5 mx-2 w-[calc(100%-1rem)]"
		} cursor-pointer transition-colors duration-200 group`;

		if (isActive) {
			return isDark
				? `${baseClasses} ${theme.activeBg} ${theme.text} shadow-md`
				: `${baseClasses} bg-hover-secondary text-text-primary`;
		}

		return isDark
			? `${baseClasses} ${theme.textSecondary} ${theme.hover} hover:shadow-sm hover:scale-102`
			: `${baseClasses} ${theme.textSecondary} hover:bg-hover-secondary hover:text-text-primary hover:shadow-sm hover:scale-102`;
	};

	const getChevronClasses = () => {
		if (isActive) {
			return isDark ? theme.text : "text-text-primary";
		}
		return isDark
			? `${theme.chevron} group-hover:${theme.text}`
			: `${theme.textSecondary} group-hover:text-text-primary`;
	};

	return (
		<li className="relative">
			<button
				type="button"
				aria-expanded={isExpanded}
				aria-controls={`${dropdownKey}-submenu`}
				className={getDropdownButtonClasses()}
				onClick={(e) => {
					e.stopPropagation();
					onToggle(item.path);
				}}
			>
				<span
					className="flex-shrink-0"
					ref={(el) => {
						iconRefs.current[dropdownKey] = el;
					}}
				>
					{item.icon}
				</span>
				{!isCollapsed && (
					<>
						<span className="ml-2.5 text-s whitespace-nowrap font-normal">
							{item.label}
						</span>
						<span className="flex-1" />
						<span className={getChevronClasses()}>
							<ChevronRight
								size={16}
								className={cn(
									"transition-transform duration-150",
									isExpanded ? "rotate-90" : "rotate-0",
								)}
							/>
						</span>
					</>
				)}
			</button>

			{/* Submenu */}
			{!isCollapsed && (
				<SubmenuList
					dropdownKey={dropdownKey}
					isExpanded={isExpanded}
					items={item.children}
					submenuRefs={submenuRefs}
					theme={theme}
					isRouteActive={isRouteActive}
				/>
			)}
		</li>
	);
};

interface SubmenuListProps {
	dropdownKey: string;
	isExpanded: boolean;
	items: NavItem[];
	submenuRefs: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
	theme: ReturnType<typeof getSidebarTheme>;
	isRouteActive: (path: string) => boolean;
}

const SubmenuList = ({
	dropdownKey,
	isExpanded,
	items,
	submenuRefs,
	theme,
	isRouteActive,
}: SubmenuListProps) => (
	<div
		id={`${dropdownKey}-submenu`}
		className={cn(
			"overflow-hidden",
			isExpanded ? "max-h-40" : "max-h-0",
			"transition-[max-height] duration-150 ease-out",
		)}
	>
		<div
			ref={(el) => {
				submenuRefs.current[dropdownKey] = el;
			}}
			className="relative"
		>
			<div
				className="absolute top-0 bottom-[6px] w-[1.5px] bg-border-secondary/70 pointer-events-none"
				style={{ left: `var(--${dropdownKey}-rail-x)` }}
			/>
			<div className="pl-[26px] pt-1 pb-1 space-y-0.5">
				{items.map((child) => {
					const isChildActive = isRouteActive(child.path);
					return (
						<NavLink
							key={child.path}
							to={child.path}
							className={({ isActive }) =>
								cn(
									"flex items-center rounded-md px-2 py-1 mx-2",
									isActive
										? "btn-active-nav"
										: cn(
												theme.textSecondary,
												"hover:bg-hover-secondary hover:text-text-primary hover:shadow-sm transition-colors duration-200",
											),
								)
							}
							onClick={(e) => e.stopPropagation()}
							aria-current={isChildActive ? "page" : undefined}
						>
							{({ isActive }) => (
								<span
									className={cn(
										"text-s whitespace-nowrap",
										isActive ? "font-semibold" : "font-normal",
									)}
								>
									{child.label}
								</span>
							)}
						</NavLink>
					);
				})}
			</div>
		</div>
	</div>
);
